import React from 'react';
import { observer } from 'mobx-react';
import { Box, Typography, Container } from '@mui/material';
import PublisherCard from '../../components/publisherCard';
import PublisherCardSkeleton from '../../components/publisherCardSkeleton';
import usePublisher from '../../../store/models/publisher/usePublisher';
import * as styles from './styles';

const SKELETON_COUNT = 3; // matches the grid's widest breakpoint (lg: 3 columns)

// Recommended is the more personalized section — once it's actually showing
// (logged in with real recommendations), Trending would just be a duplicate.
function isRecommendedSectionShowing(subscriber, recommendedPublishers) {
    return !!subscriber && recommendedPublishers.length > 0;
}

// Recommended hasn't resolved yet for a logged-in subscriber, so we don't yet
// know whether it's about to hide Trending — hold off on real content rather
// than flash it and immediately take it back once Recommended arrives.
function isRecommendedStillDeciding(subscriber, recommendedPublishersLoading, recommendedPublishers) {
    return !!subscriber && recommendedPublishersLoading && !recommendedPublishers.length;
}

function Trending({ subscriber, onSync, onShare }) {
    const {
        popularNowPublishers,
        recommendedPublishers,
        whatsHotPublisherIds,
        popularNowPublishersLoading,
        recommendedPublishersLoading,
    } = usePublisher();

    function renderSectionHeader() {
        return (
            <Box sx={styles.sectionHeader}>
                <Box>
                    <Typography sx={styles.eyebrow}>Popular right now</Typography>
                    <Typography variant="h2" sx={styles.title}>
                        Trending picks near{' '}
                        <Box component="span" sx={styles.gradientText}>you</Box>
                    </Typography>
                    <Typography sx={styles.subtitle}>
                        Here's some popular picks near you.
                    </Typography>
                </Box>
            </Box>
        );
    }

    function renderGrid() {
        return (
            <Box sx={styles.grid}>
                {popularNowPublishers.map((publisher) => (
                    <PublisherCard
                        key={publisher.pubId}
                        publisher={publisher}
                        hotRank={whatsHotPublisherIds[String(publisher.pubId)]}
                        onSync={onSync}
                        onShare={onShare}
                    />
                ))}
            </Box>
        );
    }

    function renderSkeletonGrid() {
        return (
            <Box sx={styles.grid}>
                {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                    <PublisherCardSkeleton key={i} />
                ))}
            </Box>
        );
    }

    function renderSkeletonSection() {
        return (
            <Box component="section" sx={styles.section}>
                <Container maxWidth="xl">
                    {renderSectionHeader()}
                    {renderSkeletonGrid()}
                </Container>
            </Box>
        );
    }

    if (popularNowPublishersLoading) return renderSkeletonSection();
    if (!popularNowPublishers.length) return null;
    if (isRecommendedStillDeciding(subscriber, recommendedPublishersLoading, recommendedPublishers)) return renderSkeletonSection();
    if (isRecommendedSectionShowing(subscriber, recommendedPublishers)) return null;

    return (
        <Box component="section" sx={styles.section}>
            <Container maxWidth="xl">
                {renderSectionHeader()}
                {renderGrid()}
            </Container>
        </Box>
    );
}

export default observer(Trending);
